import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import Lottie from "lottie-react";
import loginLottie from "../static/login-asset/wm-login-lottie.json";
import Logo from "../common-components/Logo";
import { Link } from "react-router-dom";
import { AppConstants } from "../constants";

type ResetPasswordFormInputs = {
  email: string;
  password: string;
  confirmPassword: string;
};

const ResetPassword: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const [requestKey, setRequestKey] = useState("");
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const currentYear = new Date().getFullYear();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, touchedFields },
  } = useForm<ResetPasswordFormInputs>({
    mode: "onChange"
  });

  useEffect(() => {
    const fetchResetData = async () => {
      try {
        const response = await fetch(`${window.location.origin}/login/forgotpassword/${key}`);
        const output = await response.json();

        if (output?.redirectUrl) {
          let redirectUrl = window.location.origin + output.redirectUrl;
          if (output.errorMessage) {
            redirectUrl += `?errorMessage=${encodeURIComponent(output.errorMessage)}`;
          }
          if (output.errorTitle) {
            redirectUrl += (redirectUrl.includes('?') ? '&' : '?') + `errorTitle=${encodeURIComponent(output.errorTitle)}`;
          }
          window.location.href = redirectUrl;
          return;
        }

        if (output?.requestKey) setRequestKey(output.requestKey);
        if (output?.j_username) {
          setEmail(output.j_username);
          setValue('email', output.j_username);
        }

        if (output?.errorMessage) setErrorMessage(output.errorMessage);
      } catch (error) {
        setErrorMessage('Error loading reset form.');
      } finally {
      }
    };

    fetchResetData();
  }, [key, setValue]);

  const onSubmit = async (data: ResetPasswordFormInputs) => {
    if (data.password !== data.confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    const formData = new URLSearchParams();
    formData.append("action", "forgotPasswordSubmit");
    formData.append("j_username", data.email);
    formData.append("j_password", data.password);
    formData.append("cj_password", data.confirmPassword);
    formData.append("RegistrationParameters.REQUEST_KEY", requestKey);
    formData.append("regButton", "Reset");

    try {
      const response = await fetch(`${window.location.origin}/login/forgotpassword`, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData.toString(),
        credentials: 'include',
      });

      const outputData = await response.json();

      if (response.status === 200 && outputData?.redirectUrl) {
        window.location.href = outputData.redirectUrl;
      } else {
        setErrorMessage(outputData?.errorMessage || "An error occurred");
      }
    } catch (err) {
      setErrorMessage("Failed to reset password.");
    }
  };

  return (

    <div className="login-container">
      <div className="row">
        <div className="row">
          <div className="login-left-section col-xs-12 col-sm-5 col-md-5 col-lg-5">
            <div className="login-content">
              <Logo />
              <form
                className="form-container login"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <div className="form-title">
                  <h1>Reset Password</h1>
                </div>
                <div className="form-row">
                  {errorMessage && (
                    <div>
                      <p className="help-block validation alert alert-danger serverError">
                        {errorMessage}
                      </p>
                    </div>
                  )}
                  <div className="row">
                    <div className="control-group">
                      <input
                        className="inputText"
                        type="email"
                        placeholder="Email"
                        readOnly
                        {...register('email')}
                        value={email}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className={`control-group ${errors.password ? 'error' : touchedFields?.password ? 'success' : ''}`}>
                      <input
                        className="small inputText"
                        type="password"
                        placeholder="New Password"
                        {...register('password', {
                          required: "Password is required",
                          minLength: { value: 6, message: "Too short: Minimum of '6' characters" },
                        })}
                      />
                      {errors.password && (
                        <p className="help-block validation alert alert-danger">
                          {errors.password.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className={`control-group ${errors.confirmPassword ? 'error' : touchedFields?.confirmPassword ? 'success' : ''}`}>
                      <input
                        className="small inputText"
                        type="password"
                        placeholder="Confirm Password"
                        {...register('confirmPassword', {
                          required: "Must match the password entered above",
                          validate: (val) => val === watch('password') || "Must match the password entered above",
                        })}

                      />
                      {errors.confirmPassword && (
                        <p className="help-block validation alert alert-danger">
                          {errors.confirmPassword.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <input
                    type="hidden"
                    name="RegistrationParameters.REQUEST_KEY"
                    value={requestKey}
                  />

                  <div className="row">
                    <div className="col-sm-12 no-padding">
                      <button
                        type="submit"
                        className="btn btn-submit pull-right col-xs-12 col-sm-12"
                      >
                        Reset
                      </button>
                    </div>
                    <div className="col-sm-12 no-padding">
                      <Link
                        to={"/"}
                        className="btn btn-block "
                      >
                        Login
                      </Link>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <p className="copyright-text">
              © {currentYear} WaveMaker. All Rights Reserved
            </p>
          </div>
          <div className="login-right-section col-xs-12 col-sm-7 col-md-7 col-lg-7">
            <div className="right-content-wrapper">
              <h3 className="right-section-title">
                {AppConstants.CAROUSELTITLE}
              </h3>
              <Lottie
                animationData={loginLottie}
                loop={true}
                className="lottie-animation"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;